const ROUTE_CONNECTOR_PREFIX = "__ROUTE_CONNECTOR__";

export const GREEN_MILESTONE_ICON =
  "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
export const RED_CHECKPOINT_ICON =
  "http://maps.google.com/mapfiles/ms/icons/red-dot.png";

export const isSameLatLng = (a, b, epsilon = 0.00001) =>
  a &&
  b &&
  Number.isFinite(a.lat) &&
  Number.isFinite(a.lng) &&
  Number.isFinite(b.lat) &&
  Number.isFinite(b.lng) &&
  Math.abs(a.lat - b.lat) < epsilon &&
  Math.abs(a.lng - b.lng) < epsilon;

export const toLatLng = (point) => {
  const lat = Number(point?.latitude ?? point?.lat);
  const lng = Number(point?.longitude ?? point?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

export const isConnectorPoint = (point) =>
  String(point?.title || "").startsWith(ROUTE_CONNECTOR_PREFIX);

/** straight when mode is straight/null/missing/manual */
export const isStraightRouteMode = (point) => {
  if (!point) return true;
  if (point.routeSource === "MANUAL") return true;

  const mode = point.routeMode;
  if (mode == null || mode === "") return true;

  return String(mode).toLowerCase() === "straight";
};

const squaredDistance = (a, b) => {
  const dLat = a.lat - b.lat;
  const dLng = a.lng - b.lng;
  return dLat * dLat + dLng * dLng;
};

export const findClosestPolylineIndex = (path, target, fromIndex = 0) => {
  let bestIndex = Math.min(fromIndex, Math.max(path.length - 1, 0));
  let bestDist = Infinity;

  for (let i = fromIndex; i < path.length; i++) {
    const dist = squaredDistance(path[i], target);
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }

  return bestIndex;
};

export const sortRoutePoints = (routePoints) =>
  Array.isArray(routePoints)
    ? [...routePoints]
        .filter((point) => toLatLng(point))
        .sort(
          (a, b) =>
            (Number(a.pointOrder) || 0) - (Number(b.pointOrder) || 0),
        )
    : [];


export const buildRouteSegments = (sortedRoutePoints, decodedPolyline = []) => {
  if (sortedRoutePoints.length < 2) {
    if (decodedPolyline.length > 1) {
      return [{ id: "fallback-polyline", path: decodedPolyline }];
    }
    return [];
  }

  const segments = [];
  let searchStart = 0;

  for (let i = 0; i < sortedRoutePoints.length - 1; i++) {
    const fromPoint = sortedRoutePoints[i];
    const toPoint = sortedRoutePoints[i + 1];
    const from = toLatLng(fromPoint);
    const to = toLatLng(toPoint);
    if (!from || !to) continue;

    const useStraight = isStraightRouteMode(toPoint);
    let path;

    if (!useStraight && decodedPolyline.length > 1) {
      const fromIdx = findClosestPolylineIndex(decodedPolyline, from, searchStart);
      const toIdx = findClosestPolylineIndex(decodedPolyline, to, fromIdx);
      searchStart = toIdx;

      const legPath = decodedPolyline.slice(fromIdx, toIdx + 1);
      path = legPath.length >= 2 ? legPath : [from, to];
    } else {
      path = [from, to];

      if (decodedPolyline.length > 1) {
        searchStart = findClosestPolylineIndex(decodedPolyline, to, searchStart);
      }
    }

    segments.push({
      id: `segment-${fromPoint.id ?? i}-${toPoint.id ?? i + 1}`,
      path,
    });
  }

  return segments;
};

/** Flatten segment paths into one continuous path (drop duplicate join points). */
export const flattenRouteSegments = (segments) => {
  const path = [];

  segments.forEach((segment) => {
    (segment.path || []).forEach((point) => {
      const last = path[path.length - 1];
      if (!last || !isSameLatLng(last, point)) {
        path.push(point);
      }
    });
  });

  return path;
};

export const decodeRoutePolyline = (routePolyline, polylineLib) => {
  if (!routePolyline || !polylineLib) return [];
  return polylineLib.decode(routePolyline).map(([lat, lng]) => ({ lat, lng }));
};
